import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Building2, ExternalLink, Plus, Settings } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import useCurrentUser from "@/hooks/use-current-user"
import { superAdminCouncilApi } from "@/lib/api"
import type { CouncilListItemDTO } from "@/gen/api"
import { hasPermission, hasReadPermission } from "@/lib/admin-permissions"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/hooks/use-toast"

type WaterBureau = CouncilListItemDTO

const environments = ["dev", "test", "prod"]
const statuses = ["active", "suspended", "inactive"]

const toTitleCase = (value?: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "N/A"

const getDisplayStatus = (council: WaterBureau) => {
  const deletionStatus = council.deletionStatus?.toUpperCase()
  return deletionStatus && deletionStatus !== "NONE" ? deletionStatus : council.status
}

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case "active":
      return "bg-green-500 text-white"
    case "suspended":
    case "delete_failed":
      return "bg-red-500 text-white"
    case "delete_requested":
    case "deleting":
      return "bg-yellow-500 text-white"
    case "deleted":
      return "bg-slate-700 text-white"
    case "inactive":
      return "bg-muted"
    default:
      return "bg-muted"
  }
}

function WaterBureauListSkeleton() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Description</TableHead>
          <TableHead>Environment</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Devices</TableHead>
          <TableHead>End Users</TableHead>
          <TableHead>Active Leak Alarms</TableHead>
          <TableHead>Admins</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {["wb-1", "wb-2", "wb-3"].map((rowKey) => (
          <TableRow key={rowKey}>
            <TableCell className="font-medium">
              <Skeleton className="h-5 w-40" />
            </TableCell>
            <TableCell className="max-w-xs">
              <Skeleton className="h-5 w-64" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-6 w-12 rounded-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-6 w-16 rounded-full" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-8" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-8" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-8" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-8" />
            </TableCell>
            <TableCell>
              <div className="flex justify-end gap-2">
                <Skeleton className="h-9 w-20 rounded" />
                <Skeleton className="h-9 w-24 rounded" />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export default function ManageWaterbureauPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { hasRole } = useCurrentUser()
  const isSuperAdmin = hasRole("super_admin")
  const canReadWaterBureaus = isSuperAdmin || hasReadPermission(user?.permissions, "water_bureaus")
  const canWriteWaterBureaus =
    isSuperAdmin ||
    hasPermission(user?.permissions, "water_bureaus", "write_own") ||
    hasPermission(user?.permissions, "water_bureaus", "write_all")
  const canRegisterWaterBureaus =
    isSuperAdmin || hasPermission(user?.permissions, "water_bureaus", "write_all")
  const { toast } = useToast()
  const [councils, setCouncils] = useState<WaterBureau[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [environmentFilter, setEnvironmentFilter] = useState("all")

  const loadCouncils = useCallback(async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()
      if (search.trim()) params.set("search", search.trim())
      if (statusFilter !== "all") params.set("status", statusFilter)
      if (environmentFilter !== "all") params.set("environment", environmentFilter)
      const response = await superAdminCouncilApi.getCouncils1(
        params.get("search") ?? undefined,
        params.get("status") ?? undefined,
        params.get("environment") ?? undefined,
      )
      setCouncils(response.data.data ?? [])
    } catch {
      toast({ title: "Error", description: "Failed to load Water Bureaus", variant: "destructive" })
    } finally {
      setIsLoading(false)
    }
  }, [environmentFilter, search, statusFilter, toast])

  useEffect(() => {
    if (!canReadWaterBureaus) return
    const timer = window.setTimeout(loadCouncils, 250)
    return () => window.clearTimeout(timer)
  }, [canReadWaterBureaus, loadCouncils])

  if (!canReadWaterBureaus) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <h2 className="text-xl font-semibold">Access denied</h2>
        <p className="mt-2 text-muted-foreground">
          You need Water Bureaus read permission to view this page.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Manage Water Bureaus</h1>
          <p className="text-muted-foreground">
            Register new Water Bureaus and manage existing ones
          </p>
        </div>
        {canRegisterWaterBureaus && (
          <Button onClick={() => navigate("/manage-waterbureau/create")}>
            <Plus className="mr-2 size-4" />
            Register New Water Bureau
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="space-y-4">
          <div className="flex items-center gap-2">
            <Building2 className="size-5 text-primary" />
            <CardTitle>Water Bureaus</CardTitle>
          </div>
          <div className="grid gap-3 md:grid-cols-[1fr_180px_180px]">
            <Input
              placeholder="Search by name, code, or description"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Select value={environmentFilter} onValueChange={setEnvironmentFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Environment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Environments</SelectItem>
                {environments.map((environment) => (
                  <SelectItem key={environment} value={environment}>
                    {environment.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {toTitleCase(status)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <WaterBureauListSkeleton />
          ) : councils.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Environment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Devices</TableHead>
                  <TableHead>End Users</TableHead>
                  <TableHead>Active Leak Alarms</TableHead>
                  <TableHead>Admins</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {councils.map((council) => {
                  const displayStatus = getDisplayStatus(council)

                  return (
                    <TableRow key={council.id}>
                      <TableCell className="font-medium">{council.name}</TableCell>
                      <TableCell className="max-w-xs truncate">
                        {council.description || council.location || "-"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{council.environment?.toUpperCase() ?? "DEV"}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(displayStatus || "")} variant="secondary">
                          {toTitleCase(displayStatus)}
                        </Badge>
                      </TableCell>
                      <TableCell>{council.devices ?? 0}</TableCell>
                      <TableCell>{council.customers ?? 0}</TableCell>
                      <TableCell>{council.activeLeakAlarms ?? 0}</TableCell>
                      <TableCell>{council.admins ?? council.owningCustomers?.length ?? 0}</TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              window.open(`/council/${council.id}`, "_blank", "noopener,noreferrer")
                            }
                          >
                            <ExternalLink className="mr-1 size-3" /> Open
                          </Button>
                          <Button
                            size="sm"
                            disabled={!council.id || !canWriteWaterBureaus}
                            onClick={() =>
                              council.id && navigate(`/manage-waterbureau/${council.id}/settings`)
                            }
                          >
                            <Settings className="mr-1 size-3" /> Settings
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          ) : (
            <p className="py-8 text-center text-muted-foreground">
              No Water Bureaus found. Click "Register New Water Bureau" to add one.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
